import { collection, doc, onSnapshot, query, runTransaction, setDoc } from 'firebase/firestore'
import { useCallback, useEffect, useState, useMemo } from 'react'
import { db } from './firebase'

export function useSellerCommissions(auctionId) {
  // Store product data to local state
  const [sellerCommissions, setSellerCommissions] = useState([])
  const [loading, setLoading] = useState(!!auctionId)

  // Sync product data
  useEffect(() => {
    if (!auctionId) return
    const q = query(collection(db, `auctions/${auctionId}/sellerCommissions`))
    setLoading(true)

    // Subscribe to data from /sellerCommissions
    const unsubscribe = onSnapshot(
      // Refetence (or path) to the data in Firestore.
      q,
      // This callback is called when data is initially fetched and when it changes.
      querySnapshot => {
        const data = []
        querySnapshot.forEach(doc => {
          data.push({ ...doc.data(), id: doc.id })
        })

        // Set sellerCommissions data to local state in this hook.
        setSellerCommissions(data)
        setLoading(false)
      },
      error => {
        console.error('Failed to sync Product data', error)
      }
    )

    return () => {
      // Unsubscribe to change callbacks when the hook unmounts
      unsubscribe()
    }
  }, [auctionId])

  // These functions mutate data in Firestore.

  return {
    // Products data from Firebase
    sellerCommissions,
    loading,
  }
}

export function useSellerProducts(auctionId, sellerCommissionId) {
  // Store product data to local state
  const [sellerProducts, setSellerProducts] = useState([])
  const [loading, setLoading] = useState(!!auctionId)

  // Sync product data
  useEffect(() => {
    if (!auctionId) return
    const q = query(collection(db, `auctions/${auctionId}/sellerCommissions/${sellerCommissionId}/sellerProducts`))
    setLoading(true)

    // Subscribe to data from /sellerCommissions
    const unsubscribe = onSnapshot(
      // Refetence (or path) to the data in Firestore.
      q,
      // This callback is called when data is initially fetched and when it changes.
      querySnapshot => {
        const data = []
        querySnapshot.forEach(doc => {
          data.push({ ...doc.data(), id: doc.id })
        })

        // Set sellerCommissions data to local state in this hook.
        setSellerProducts(data)
        setLoading(false)
      },
      error => {
        console.error('Failed to sync Product data', error)
      }
    )

    return () => {
      // Unsubscribe to change callbacks when the hook unmounts
      unsubscribe()
    }
  }, [auctionId, sellerCommissionId])

  // These functions mutate data in Firestore.

  return {
    // Products data from Firebase
    sellerProducts,
    loading,
  }
}

export function useSellerCommission(auctionId, sellerCommissionId) {
  // Store product data to local state
  const [sellerCommission, setSellerCommission] = useState(null)
  const [loading, setLoading] = useState(!!auctionId)
  const docRef = useMemo(
    () =>
      auctionId && sellerCommissionId ? doc(db, `auctions/${auctionId}/sellerCommissions`, sellerCommissionId) : null,
    [auctionId, sellerCommissionId]
  )

  // Sync product data
  useEffect(() => {
    if (!docRef) return
    setLoading(true)

    // Subscribe to data from /sellerCommissions
    const unsubscribe = onSnapshot(
      // Refetence (or path) to the data in Firestore.
      docRef,
      // This callback is called when data is initially fetched and when it changes.
      doc => {
        // Set sellerCommissions data to local state in this hook.
        setSellerCommission({ ...doc.data(), id: doc.id })
        setLoading(false)
      },
      error => {
        console.error('Failed to sync Product data', error)
      }
    )

    return () => {
      // Unsubscribe to change callbacks when the hook unmounts
      unsubscribe()
    }
  }, [docRef])

  // These functions mutate data in Firestore.
  const updateSellerCommission = useCallback(
    async data => {
      await setDoc(docRef, data, { merge: true })
    },
    [docRef]
  )

  return {
    // Products data from Firebase
    sellerCommission,
    loading,
    updateSellerCommission,
  }
}

export const useToggleSetCompensted = () => {
  const [loading, setLoading] = useState(false)

  const toggleCompensated = useCallback(async (auctionId, sellerCommissionId) => {
    setLoading(true)
    const docRef = doc(db, `auctions/${auctionId}/sellerCommissions`, sellerCommissionId)
    await runTransaction(db, async transaction => {
      const doc = await transaction.get(docRef)
      if (!doc.exists()) {
        throw new Error('Seller commission not found')
      }
      const { isCompensated } = doc.data()

      transaction.set(docRef, { isCompensated: !isCompensated }, { merge: true })
    })
    setLoading(false)
  }, [])

  return [toggleCompensated, loading]
}
